import type { Repository, Scan, Issue } from '../../store/api/githubApi';

export type ImprovementObjectiveType = 'overall' | 'security' | 'quality' | 'debt';

export interface SimulatedImpact {
  currentHealth: number;
  projectedHealth: number;
  healthDelta: number;
  currentCritical: number;
  projectedCritical: number;
  currentHigh: number;
  projectedHigh: number;
  currentTotal: number;
  projectedTotal: number;
  selectedFixesCount: number;
  estimatedEffortMinutes: number;
  estimatedEffortFormatted: string;
  securityImpact: 'High' | 'Medium' | 'Low' | 'Minimal';
  debtImpact: 'High' | 'Medium' | 'Low';
  filesAffectedCount: number;
}

/**
 * Parses estimated fix time string (e.g. '45m', '1 hr', '30 min', '2 days')
 * into integer minutes. If missing or null, falls back to a standard
 * conservative estimate based on severity.
 */
export function parseEffortMinutes(issue: Issue): number {
  const raw = issue.ai_estimated_fix_time;
  if (raw) {
    const s = raw.toLowerCase().trim();
    if (s.includes('day')) {
      const match = s.match(/(\d+)/);
      if (match) return parseInt(match[1], 10) * 8 * 60;
    }
    const range = s.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (range) {
      const avg = (parseInt(range[1], 10) + parseInt(range[2], 10)) / 2;
      return s.includes('min') ? Math.round(avg) : Math.round(avg * 60);
    }
    if (s.includes('min')) {
      const match = s.match(/(\d+)/);
      if (match) return parseInt(match[1], 10);
    }
    if (s.includes('h') || s.includes('hr')) {
      const match = s.match(/(\d+(?:\.\d+)?)/);
      if (match) return Math.round(parseFloat(match[1]) * 60);
    }
  }

  // Fallback based on severity when ai_estimated_fix_time is not provided
  switch (issue.severity?.toLowerCase()) {
    case 'critical':
      return 45;
    case 'high':
      return 30;
    case 'medium':
      return 20;
    case 'low':
      return 15;
    default:
      return 15;
  }
}

/**
 * Formats minutes into human-readable format like "4h 15m" or "45 min".
 */
export function formatEffortMinutes(totalMinutes: number): string {
  if (totalMinutes <= 0) return '0 min';
  if (totalMinutes < 60) return `~${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) return `~${hours}h`;
  return `~${hours}h ${mins}m`;
}

/**
 * Determines impact badge level (HIGH / MEDIUM / LOW) from existing issue metadata.
 */
export function getIssueImpactLevel(issue: Issue): 'HIGH' | 'MEDIUM' | 'LOW' {
  const sev = (issue.severity ?? '').toLowerCase();
  const score = issue.ai_priority_score ?? 0;
  const isSecurity = issue.category === 'security' || issue.category === 'dependencies' || Boolean(issue.cve_id);

  if (sev === 'critical' || (isSecurity && sev === 'high') || score >= 80) {
    return 'HIGH';
  }
  if (sev === 'high' || (isSecurity && sev === 'medium') || score >= 50) {
    return 'MEDIUM';
  }
  return 'LOW';
}

/**
 * Sorts and prioritizes findings based on the user's selected improvement objective.
 */
export function sortIssuesByObjective(issues: Issue[], objective: ImprovementObjectiveType): Issue[] {
  // Only include active issues
  const active = issues.filter((i) => i.status !== 'ignored' && i.status !== 'pr_merged');

  const severityRank: Record<string, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
    info: 0,
  };

  const getSev = (i: Issue) => severityRank[(i.severity ?? '').toLowerCase()] ?? 0;
  const isSecurity = (i: Issue) =>
    i.category === 'security' || i.category === 'dependencies' || Boolean(i.cve_id) || Boolean(i.owasp_category);
  const isQuality = (i: Issue) =>
    i.category === 'complexity' || i.category === 'code' || i.category === 'documentation' || i.category === 'iac';
  const isDebt = (i: Issue) =>
    i.category === 'debt' || i.category === 'complexity' || i.category === 'dependencies';

  return [...active].sort((a, b) => {
    switch (objective) {
      case 'security': {
        const aSec = isSecurity(a) ? 1 : 0;
        const bSec = isSecurity(b) ? 1 : 0;
        if (aSec !== bSec) return bSec - aSec;
        const sevDiff = getSev(b) - getSev(a);
        if (sevDiff !== 0) return sevDiff;
        return (b.ai_priority_score ?? 0) - (a.ai_priority_score ?? 0);
      }
      case 'quality': {
        const aQual = isQuality(a) ? 1 : 0;
        const bQual = isQuality(b) ? 1 : 0;
        if (aQual !== bQual) return bQual - aQual;
        const sevDiff = getSev(b) - getSev(a);
        if (sevDiff !== 0) return sevDiff;
        return (b.ai_priority_score ?? 0) - (a.ai_priority_score ?? 0);
      }
      case 'debt': {
        const aDebt = isDebt(a) ? 1 : 0;
        const bDebt = isDebt(b) ? 1 : 0;
        if (aDebt !== bDebt) return bDebt - aDebt;
        const prioDiff = (b.ai_priority_score ?? 0) - (a.ai_priority_score ?? 0);
        if (prioDiff !== 0) return prioDiff;
        return getSev(b) - getSev(a);
      }
      case 'overall':
      default: {
        const sevDiff = getSev(b) - getSev(a);
        if (sevDiff !== 0) return sevDiff;
        const prioDiff = (b.ai_priority_score ?? 0) - (a.ai_priority_score ?? 0);
        if (prioDiff !== 0) return prioDiff;
        return 0;
      }
    }
  });
}

/**
 * Computes the simulated impact of resolving the selected issues.
 * Uses realistic severity weighting derived from existing findings data.
 * Does not fabricate guaranteed outcomes.
 */
export function computeImpactSimulation(
  repo: Repository,
  scan: Scan | null | undefined,
  selectedIssues: Issue[]
): SimulatedImpact {
  const currentHealth = Math.round(scan?.debt_score ?? repo.latest_debt_score ?? 60);
  const currentCritical = scan?.count_critical ?? repo.latest_count_critical ?? 0;
  const currentHigh = scan?.count_high ?? repo.latest_count_high ?? 0;
  const allIssuesCount = scan?.issues?.length ?? (currentCritical + currentHigh + (scan?.count_medium ?? 0) + (scan?.count_low ?? 0));
  const currentTotal = Math.max(allIssuesCount, currentCritical + currentHigh);

  const selectedCritical = selectedIssues.filter((i) => (i.severity ?? '').toLowerCase() === 'critical').length;
  const selectedHigh = selectedIssues.filter((i) => (i.severity ?? '').toLowerCase() === 'high').length;
  const selectedMedium = selectedIssues.filter((i) => (i.severity ?? '').toLowerCase() === 'medium').length;
  const selectedLow = selectedIssues.filter((i) => (i.severity ?? '').toLowerCase() === 'low').length;

  const projectedCritical = Math.max(0, currentCritical - selectedCritical);
  const projectedHigh = Math.max(0, currentHigh - selectedHigh);
  const projectedTotal = Math.max(0, currentTotal - selectedIssues.length);

  // Health calculation:
  // Critical issues: ~4.5 points
  // High issues: ~2.2 points
  // Medium issues: ~0.8 points
  // Low issues: ~0.25 points
  const rawGain =
    selectedCritical * 4.5 +
    selectedHigh * 2.2 +
    selectedMedium * 0.8 +
    selectedLow * 0.25;

  const headroom = 100 - currentHealth;
  let healthDelta = 0;
  if (selectedIssues.length > 0 && headroom > 0) {
    healthDelta = Math.min(headroom, Math.max(1, Math.round(rawGain)));
  }
  const projectedHealth = Math.min(100, currentHealth + healthDelta);

  // Effort
  const totalMinutes = selectedIssues.reduce((sum, i) => sum + parseEffortMinutes(i), 0);
  const estimatedEffortFormatted = formatEffortMinutes(totalMinutes);

  // Security impact
  const securityIssuesCount = selectedIssues.filter(
    (i) => i.category === 'security' || i.category === 'dependencies' || Boolean(i.cve_id)
  ).length;
  let securityImpact: 'High' | 'Medium' | 'Low' | 'Minimal' = 'Minimal';
  if (selectedCritical > 0 || securityIssuesCount >= 2) {
    securityImpact = 'High';
  } else if (securityIssuesCount >= 1 || selectedHigh > 0) {
    securityImpact = 'Medium';
  } else if (selectedIssues.length > 0) {
    securityImpact = 'Low';
  }

  // Debt impact
  const debtOrComplexityCount = selectedIssues.filter(
    (i) => i.category === 'complexity' || i.category === 'iac' || i.category === 'debt'
  ).length;
  let debtImpact: 'High' | 'Medium' | 'Low' = 'Low';
  if (debtOrComplexityCount >= 2 || selectedIssues.length >= 4) {
    debtImpact = 'High';
  } else if (debtOrComplexityCount >= 1 || selectedIssues.length >= 2) {
    debtImpact = 'Medium';
  }

  // Files affected
  const uniqueFiles = new Set(selectedIssues.map((i) => i.file_path).filter(Boolean));
  const filesAffectedCount = uniqueFiles.size;

  return {
    currentHealth,
    projectedHealth,
    healthDelta,
    currentCritical,
    projectedCritical,
    currentHigh,
    projectedHigh,
    currentTotal,
    projectedTotal,
    selectedFixesCount: selectedIssues.length,
    estimatedEffortMinutes: totalMinutes,
    estimatedEffortFormatted,
    securityImpact,
    debtImpact,
    filesAffectedCount,
  };
}
